import { type ButtonHTMLAttributes, forwardRef } from 'react'

// Button variants compose directly onto the design tokens defined
// in src/styles/tokens.css; no raw stone-* or fern-* utilities allowed.

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'warning'
type Size = 'sm' | 'md'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  pill?: boolean
}

const BASE =
  'inline-flex items-center justify-center font-medium transition select-none ' +
  'disabled:cursor-not-allowed disabled:opacity-60 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ' +
  'focus-visible:ring-[var(--xr-brand-primary)] focus-visible:ring-offset-[var(--xr-bg-canvas)]'

const SIZE_CLS: Record<Size, string> = {
  sm: 'h-[26px] px-2.5 text-[11px]',
  md: 'h-[30px] px-3.5 text-xs',
}

const VARIANT_CLS: Record<Variant, string> = {
  primary:
    'bg-[var(--xr-brand-primary)] text-[var(--xr-brand-primary-ink)] hover:bg-[var(--xr-brand-primary-hover)]',
  secondary:
    'bg-transparent text-[var(--xr-text-primary)] border border-[var(--xr-border-default)] hover:bg-[var(--xr-bg-surface-elev)]',
  ghost: 'bg-transparent text-[var(--xr-text-primary)] hover:bg-[var(--xr-bg-surface-elev)]',
  danger:
    'bg-transparent text-[var(--xr-danger)] border border-[var(--xr-danger)] hover:bg-[var(--xr-danger-soft)]',
  warning:
    'bg-transparent text-[var(--xr-accent)] border border-[var(--xr-accent)] hover:bg-[var(--xr-accent-soft)]',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'sm', pill = true, className = '', ...rest },
  ref,
) {
  const radius = pill ? 'rounded-[var(--xr-radius-full)]' : 'rounded-[var(--xr-radius-md)]'
  const cls = `${BASE} ${SIZE_CLS[size]} ${VARIANT_CLS[variant]} ${radius} ${className}`.trim()
  return <button ref={ref} className={cls} {...rest} />
})
